'use client';

import React, { useMemo } from 'react';

/*
 * Detect if we're running React 19+ which supports boolean inert attribute.
 * React 18 requires an empty string "" for boolean attributes.
 */
const isReact19OrHigher = Number.parseInt(React.version.split('.')[0], 10) >= 19;

/**
 * Returns props object with the inert attribute set to the proper format
 * for the current React version, or an empty object if not inert.
 *
 * React 19+ accepts boolean true/undefined for the inert attribute.
 * React 18 requires empty string "" for true, undefined for false.
 *
 * @param shouldBeInert - Boolean indicating whether the element should be inert
 * @returns Props object: Record<string, never> | { inert: true } | { inert: '' }
 *
 * @example
 * ```tsx
 * const MyComponent = ({ disabled }) => {
 *   const inertProps = useInertProps(disabled);
 *   return <div {...inertProps}>Content</div>;
 * };
 * ```
 *
 * @example
 * ```tsx
 * // For dynamic values in callbacks, compute both ahead of time:
 * const MyCarousel = () => {
 *   const inertPropsWhenHidden = useInertProps(true);
 *   const inertPropsWhenVisible = useInertProps(false);
 *
 *   const renderSlide = useCallback((isHidden) => {
 *     const inertProps = isHidden ? inertPropsWhenHidden : inertPropsWhenVisible;
 *     return <div {...inertProps}>Slide</div>;
 *   }, [inertPropsWhenHidden, inertPropsWhenVisible]);
 * };
 * ```
 */
export const useInertProps = (
  shouldBeInert: boolean | undefined,
): Record<string, never> | { inert: true } | { inert: '' } => {
  return useMemo(() => {
    if (!shouldBeInert) {
      return {} as Record<string, never>;
    }
    return isReact19OrHigher ? { inert: true } : { inert: '' };
  }, [shouldBeInert]);
};
